import { Controller } from "react-hook-form";

import { TextInput, UISwitch } from "common/components";
import { ApiErrorMessage } from "common/components/ApiErrorMessage/ApiErrorMessage";
import { Select } from "common/components/form/Select";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getSiteLanguage } from "domains/language/selectors";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import * as Styled from "domains/moderate/sources/components/AddSourceFormModal/AddSourceFormModal.styled";
import { useAddSourceForm } from "domains/moderate/sources/components/AddSourceFormModal/useAddSourceForm";
import { SourceLogoImagePicker } from "domains/moderate/sources/components/SourceLogoImagePicker/SourceLogoImagePicker";
import {
  AUTOPILOT_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  COUNTRY_OPTIONS,
  GREATER_REGION_OPTIONS,
  LICENSE_OPTIONS,
  PUBLICATION_TYPE_OPTIONS,
  REGION_OPTIONS,
  SOURCE_TYPE_OPTIONS,
} from "domains/moderate/sources/constants";

export const AddSourceFormModal = () => {
  const { control, handleSubmit, onSubmit, error, setRegionAndGreaterRegionOnCountryChange } = useAddSourceForm();
  const translate = useTranslate();
  const siteLanguage = useSelector(getSiteLanguage);

  return (
    <Styled.Form key={siteLanguage} onSubmit={handleSubmit(onSubmit, (error) => console.log(error))}>
      <ModalHeader title={translate("app.moderate.sources.add.title")} />
      <ApiErrorMessage error={error} />
      <Styled.FormBody>
        <Controller
          control={control}
          name="name"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="name" className="required">
                {translate("app.moderate.sources.name.label")}
                <Styled.RequiredAsterisk> *</Styled.RequiredAsterisk>
              </Styled.Label>
              <TextInput name="name" value={value} onChange={onChange} hasError={!!error} />
              <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="name" className="required">
                {translate("app.moderate.sources.description.label")}
              </Styled.Label>
              <TextInput name="description" value={value} onChange={onChange} hasError={!!error} />
              <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="url"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="url" className="required">
                {translate("app.moderate.sources.url.label")}
                <Styled.RequiredAsterisk> *</Styled.RequiredAsterisk>
              </Styled.Label>
              <TextInput name="url" value={value} onChange={onChange} hasError={!!error} />
              <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="active"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="status-switch" className="required">
                {translate("app.moderate.sources.status.label")}
                <Styled.RequiredAsterisk> *</Styled.RequiredAsterisk>
              </Styled.Label>
              <Styled.StatusSwitchContainer>
                <Styled.StatusTag isActive={value}>{value ? "Active" : "Inactive"}</Styled.StatusTag>
                <UISwitch
                  id="status-switch"
                  isOn={value}
                  onClick={(e) => {
                    e.preventDefault();
                    onChange(!value);
                  }}
                />
              </Styled.StatusSwitchContainer>
              <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="logoUrl"
          render={({ field: { value, onChange } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="logo-url-picker">{translate("app.moderate.sources.logo.label")}</Styled.Label>
              <SourceLogoImagePicker accessibilityId="logo-url-picker" imageUrl={value} onSetImageUrl={onChange} />
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="license"
          render={({ field: { value, onChange } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="license-select">{translate("app.moderate.sources.license.label")}</Styled.Label>
              <Select
                accessibilityId="license-select"
                value={value}
                placeholder={translate("app.moderate.sources.license.placeholder")}
                options={LICENSE_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="autopilot"
          render={({ field: { value, onChange } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="autopilot-select">
                {translate("app.moderate.sources.autopilot.label")}
              </Styled.Label>
              <Select
                accessibilityId="autopilot-select"
                value={value}
                placeholder={translate("app.moderate.sources.autopilot.placeholder")}
                options={AUTOPILOT_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="type"
          render={({ field: { value, onChange } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="type-select">{translate("app.moderate.sources.type.label")}</Styled.Label>
              <Select
                accessibilityId="type-select"
                placeholder={translate("app.moderate.sources.type.placeholder")}
                value={value}
                options={SOURCE_TYPE_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="contentType"
          render={({ field: { value, onChange } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="content-type-select">
                {translate("app.moderate.sources.contentType.label")}
              </Styled.Label>
              <Select
                accessibilityId="content-type-select"
                placeholder={translate("app.moderate.sources.contentType.placeholder")}
                value={value}
                options={CONTENT_TYPE_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="publicationType"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="publication-type-select">
                {translate("app.moderate.sources.publicationType.label")}
                <Styled.RequiredAsterisk> *</Styled.RequiredAsterisk>
              </Styled.Label>
              <Select
                accessibilityId="publication-type-select"
                placeholder={translate("app.moderate.sources.publicationType.placeholder")}
                value={value}
                options={PUBLICATION_TYPE_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
                hasError={!!error}
              />
              <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="country"
          render={({ field: { value, onChange } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="country-type-select">
                {translate("app.moderate.sources.country.label")}
              </Styled.Label>
              <Select
                accessibilityId="country-type-select"
                placeholder={translate("app.moderate.sources.country.placeholder")}
                value={value}
                options={COUNTRY_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => {
                    onChange(option.value === value?.value ? undefined : option);
                    setRegionAndGreaterRegionOnCountryChange(option.value);
                  },
                }))}
              />
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="region"
          render={({ field: { value, onChange } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="region-type-select">{translate("app.moderate.sources.region.label")}</Styled.Label>
              <Select
                accessibilityId="region-type-select"
                placeholder={translate("app.moderate.sources.region.placeholder")}
                value={value}
                options={REGION_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
                isDisabled
              />
            </Styled.FormFieldContainer>
          )}
        />
        <Controller
          control={control}
          name="greaterRegion"
          render={({ field: { value, onChange } }) => (
            <Styled.FormFieldContainer>
              <Styled.Label htmlFor="greater-region-type-select">
                {translate("app.moderate.sources.greaterRegion.label")}
              </Styled.Label>
              <Select
                accessibilityId="greater-region-type-select"
                placeholder={translate("app.moderate.sources.greaterRegion.placeholder")}
                value={value}
                options={GREATER_REGION_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
                isDisabled
              />
            </Styled.FormFieldContainer>
          )}
        />
      </Styled.FormBody>
      <Styled.CreateSourceButton type="submit">
        {translate("app.moderate.sources.create.button")}
      </Styled.CreateSourceButton>
    </Styled.Form>
  );
};
